import { useState, useEffect, useCallback } from 'react';

const FIELDS = [
  { key: 'fullName', label: 'Full Name',    placeholder: 'e.g. Jane Smith',           type: 'text' },
  { key: 'role',     label: 'Role / Title', placeholder: 'e.g. Solutions Engineer',   type: 'text' },
  { key: 'phone',    label: 'Phone Number', placeholder: 'e.g. +1 (555) 000-0000',    type: 'tel'  },
];

const STORAGE_KEY = 'raah_profile_nudge';
const THIRTY_DAYS = 30 * 24 * 60 * 60 * 1000;

function getNudgeState() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
  } catch {
    return {};
  }
}

function setNudgeState(state) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
}

// Returns the next field that should be prompted
function getNextField(profile, nudgeState) {
  for (const field of FIELDS) {
    const value = profile[field.key];
    const meta  = nudgeState[field.key] || {};

    // Never filled — always prompt
    if (!value) return field;

    // Filled but dismissed > 30 days ago — reprompt
    if (value && meta.lastDismissed) {
      const elapsed = Date.now() - meta.lastDismissed;
      if (elapsed > THIRTY_DAYS) return field;
    }
  }
  return null;
}

// % of FIELDS that have a value
export function profileCompleteness(profile) {
  if (!profile) return 0;
  // email always comes from Auth0 so count it separately
  const filled = FIELDS.filter(f => !!profile[f.key]).length;
  return Math.round(((filled + (profile.email ? 1 : 0)) / (FIELDS.length + 1)) * 100);
}

export function useProfileNudge(profile, getToken, apiUrl) {
  const [activeField, setActiveField]     = useState(null); // field to prompt
  const [saving, setSaving]               = useState(false);
  const [value, setValue]                 = useState('');
  const [bannerDismissed, setBannerDismissed] = useState(false);

  // On mount / when profile loads, determine which field to nudge
  useEffect(() => {
    if (!profile) return;
    const nudgeState = getNudgeState();
    const field = getNextField(profile, nudgeState);
    if (field) {
      setActiveField(field);
      setValue(profile[field.key] || '');
    }
  }, [profile]);

  const dismiss = useCallback(() => {
    if (!activeField) return;
    const nudgeState = getNudgeState();
    nudgeState[activeField.key] = { ...nudgeState[activeField.key], lastDismissed: Date.now() };
    setNudgeState(nudgeState);
    setActiveField(null);
  }, [activeField]);

  const save = useCallback(async (onSuccess) => {
    if (!activeField || !value.trim()) return;
    setSaving(true);
    try {
      const token = await getToken();
      const res   = await fetch(`${apiUrl}api/profile/update`, {
        method:  'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body:    JSON.stringify({ [activeField.key]: value.trim() }),
      });
      if (!res.ok) throw new Error('Failed to save.');

      // Mark as just filled — won't reprompt for 30 days
      const nudgeState = getNudgeState();
      nudgeState[activeField.key] = { lastDismissed: Date.now() };
      setNudgeState(nudgeState);

      setActiveField(null);
      if (onSuccess) onSuccess({ [activeField.key]: value.trim() });
    } catch (err) {
      console.error('Profile update error:', err.message);
    } finally {
      setSaving(false);
    }
  }, [activeField, value, getToken, apiUrl]);

  return {
    activeField,
    value, setValue,
    saving,
    dismiss,
    save,
    bannerDismissed, setBannerDismissed,
  };
}